// Clientes: la ficha completa. No es un CRM (SKILL.md sección 7).

import { api } from "../api";
import { esc } from "../html";
import { iconoMas, iconoChevron } from "../iconos";
import { ETIQUETA_ESTADO_PRESUPUESTO, claseEstadoPresupuesto } from "../etiquetas";
import type { Cliente, Presupuesto } from "../../lib/tipos";
import { formatearMonto } from "../../lib/calculo";

interface ClienteListado extends Cliente {
  proyectos: number;
  presupuestos: number;
}

interface FichaCliente {
  cliente: Cliente;
  presupuestos: Presupuesto[];
  proyectos: { id: number; nombre: string }[];
}

function ficha(c: ClienteListado): string {
  return `
    <details class="fila" data-id="${c.id}">
      <summary class="flex items-center gap-3 p-3">
        <div class="min-w-0 flex-1">
          <div class="truncate font-medium">${esc(c.nombre)}</div>
          <div class="mt-0.5 truncate text-xs text-apagado">
            ${c.contacto ? esc(c.contacto) : "sin contacto"}
          </div>
        </div>
        <span class="shrink-0 text-xs text-apagado">
          ${c.proyectos} proy · ${c.presupuestos} pres
        </span>
        <span class="shrink-0 text-apagado">${iconoChevron}</span>
      </summary>
      <div class="detalle border-t border-borde p-3 text-sm text-apagado">Cargando…</div>
    </details>`;
}

function formulario(f: FichaCliente): string {
  const c = f.cliente;
  return `
    <form class="fEditar space-y-3" data-id="${c.id}">
      <label><span class="etiqueta">Nombre</span>
        <input name="nombre" class="campo" value="${esc(c.nombre)}" maxlength="120" required /></label>
      <div class="grid grid-cols-2 gap-3">
        <label><span class="etiqueta">Contacto</span>
          <input name="contacto" class="campo" value="${esc(c.contacto)}" maxlength="200" /></label>
        <label><span class="etiqueta">Teléfono</span>
          <input name="telefono" class="campo" value="${esc(c.telefono)}" maxlength="60" /></label>
      </div>
      <label><span class="etiqueta">Email</span>
        <input name="email" class="campo" value="${esc(c.email)}" maxlength="200" /></label>
      <label><span class="etiqueta">Notas</span>
        <textarea name="notas" class="campo" rows="2" maxlength="4000">${esc(c.notas)}</textarea></label>

      ${
        f.proyectos.length
          ? `<div><span class="etiqueta">Proyectos</span>
               <div class="flex flex-wrap gap-1.5">
                 ${f.proyectos
                   .map(
                     (p) =>
                       `<a href="#/proyecto/${p.id}" class="chip hover:text-texto">${esc(p.nombre)}</a>`,
                   )
                   .join("")}
               </div></div>`
          : ""
      }

      ${
        f.presupuestos.length
          ? `<div><span class="etiqueta">Presupuestos</span>
               <div class="space-y-1">
                 ${f.presupuestos
                   .map(
                     (p) =>
                       `<a href="#/presupuesto/${p.id}" class="flex items-center gap-2 text-sm hover:text-texto">
                          <span class="font-mono text-xs text-apagado">Nº ${p.numero}</span>
                          <span class="flex-1">${formatearMonto(p.total, p.moneda)}</span>
                          <span class="${claseEstadoPresupuesto(p.estado_efectivo)}">${
                            ETIQUETA_ESTADO_PRESUPUESTO[p.estado_efectivo]
                          }</span>
                        </a>`,
                   )
                   .join("")}
               </div></div>`
          : ""
      }

      <p class="err text-sm text-alerta"></p>
      <div class="flex gap-2">
        <button class="btn-primario flex-1">Guardar</button>
        <button type="button" class="bBaja btn-sec" data-id="${c.id}">Dar de baja</button>
      </div>
    </form>`;
}

export async function renderClientes(contenedor: HTMLElement): Promise<void> {
  async function pintar(): Promise<void> {
    const clientes = await api.get<ClienteListado[]>("/api/clientes");

    contenedor.innerHTML = `
      <h1 class="titulo-pagina">Clientes</h1>
      <p class="subtitulo">${clientes.length} ${clientes.length === 1 ? "activo" : "activos"}</p>

      <div class="mt-4 space-y-2">
        ${
          clientes.length
            ? clientes.map(ficha).join("")
            : `<div class="tarjeta text-center text-apagado">Todavía no hay clientes.</div>`
        }
      </div>

      <details class="tarjeta mt-4">
        <summary class="flex items-center gap-2 font-medium text-acento">${iconoMas} Cliente nuevo</summary>
        <form id="fNuevo" class="mt-4 space-y-3">
          <label><span class="etiqueta">Nombre</span>
            <input id="nombre" class="campo" required maxlength="120" /></label>
          <div class="grid grid-cols-2 gap-3">
            <label><span class="etiqueta">Contacto</span>
              <input id="contacto" class="campo" maxlength="200" /></label>
            <label><span class="etiqueta">Teléfono</span>
              <input id="telefono" class="campo" maxlength="60" /></label>
          </div>
          <label><span class="etiqueta">Email</span>
            <input id="email" class="campo" maxlength="200" /></label>
          <p id="errNuevo" class="text-sm text-alerta"></p>
          <button class="btn-primario w-full">Crear</button>
        </form>
      </details>`;

    // La ficha se trae recién al abrir: la lista no necesita los presupuestos.
    contenedor.querySelectorAll<HTMLDetailsElement>("details[data-id]").forEach((d) => {
      d.addEventListener("toggle", async () => {
        if (!d.open || d.dataset.cargado) return;
        d.dataset.cargado = "1";
        const caja = d.querySelector(".detalle") as HTMLElement;
        try {
          const f = await api.get<FichaCliente>(`/api/clientes/${d.dataset.id}`);
          caja.innerHTML = formulario(f);
          enganchar(caja);
        } catch (e) {
          caja.innerHTML = `<span class="text-alerta">${esc(
            e instanceof Error ? e.message : "error",
          )}</span>`;
        }
      });
    });

    const form = contenedor.querySelector("#fNuevo") as HTMLFormElement;
    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      const err = contenedor.querySelector("#errNuevo") as HTMLElement;
      err.textContent = "";
      const dato = (id: string) => (contenedor.querySelector("#" + id) as HTMLInputElement).value.trim();
      try {
        await api.post("/api/clientes", {
          nombre: dato("nombre"),
          contacto: dato("contacto") || null,
          telefono: dato("telefono") || null,
          email: dato("email") || null,
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo crear";
      }
    });
  }

  function enganchar(caja: HTMLElement): void {
    const form = caja.querySelector(".fEditar") as HTMLFormElement;
    const err = caja.querySelector(".err") as HTMLElement;

    form.addEventListener("submit", async (ev) => {
      ev.preventDefault();
      err.textContent = "";
      const d = new FormData(form);
      const texto = (k: string) => String(d.get(k) ?? "").trim() || null;
      try {
        await api.put(`/api/clientes/${form.dataset.id}`, {
          nombre: String(d.get("nombre") ?? "").trim(),
          contacto: texto("contacto"),
          telefono: texto("telefono"),
          email: texto("email"),
          notas: texto("notas"),
        });
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo guardar";
      }
    });

    caja.querySelector(".bBaja")?.addEventListener("click", async () => {
      if (!confirm("¿Dar de baja al cliente? Sus presupuestos quedan guardados.")) return;
      try {
        await api.del(`/api/clientes/${form.dataset.id}`);
        await pintar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo dar de baja";
      }
    });
  }

  try {
    await pintar();
  } catch (e) {
    contenedor.innerHTML = `<div class="tarjeta text-alerta">No se pudieron cargar los clientes: ${esc(
      e instanceof Error ? e.message : "error desconocido",
    )}</div>`;
  }
}
